// Mieter-Detail (Handoff Mieter §5.3): Kopf, Widget-Werte und die deterministische
// KI-Zusammenfassung aus Fakten. Reine Funktionen – testbar.

import { eur } from '../immobilien/format';
import { deDatum } from '../immobilien/objektDetail';
import { RUECKSTAND_SCHWELLE } from '../mietkonto/mietkonto';

export type MieterGruppe = 'fin' | 'verm' | 'komm' | 'haus';
export type GruppenFilter = 'alle' | MieterGruppe;

export const MIETER_GRUPPEN: readonly { key: GruppenFilter; label: string }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'fin', label: 'Finanzen' },
  { key: 'verm', label: 'Vermietung' },
  { key: 'komm', label: 'Kommunikation' },
  { key: 'haus', label: 'Wohnung & Technik' },
];

export function istGruppenFilter(v: string | null): v is GruppenFilter {
  return MIETER_GRUPPEN.some(g => g.key === v);
}

/**
 * Kicker „FRAU · MIETER SEIT …“. In den Altdaten steht die Briefanrede
 * („Sehr geehrte Frau Beck“, „Sehr geehrte Eheleute …“) – daraus die Kurzform.
 */
export function anredeKurz(anrede: string | null | undefined): string | null {
  const a = (anrede ?? '').toLowerCase();
  if (!a.trim()) return null;
  if (/\bfrau\b/.test(a) && /\bherr\b/.test(a)) return 'Frau & Herr';
  if (/\bfrau\b/.test(a)) return 'Frau';
  if (/\bherr\b/.test(a)) return 'Herr';
  if (/\bdivers\b/.test(a)) return 'Divers';
  if (/\beheleute\b/.test(a)) return 'Eheleute';
  if (/\bfamilie\b/.test(a)) return 'Familie';
  return null;
}

/** „7 Jahre 6 Mon.“, „5 Mon.“, „1 Jahr“, „unter 1 Mon.“ (Prototyp-Schreibweise). */
export function mietdauerKurz(beginn: string | null | undefined, heute: string): string {
  if (!beginn) return '–';
  const [j1, m1, t1] = beginn.slice(0, 10).split('-').map(Number);
  const [j2, m2, t2] = heute.slice(0, 10).split('-').map(Number);
  if (!j1 || !m1 || !j2 || !m2) return '–';
  const gesamt = (j2 - j1) * 12 + (m2 - m1) - ((t2 ?? 1) < (t1 ?? 1) ? 1 : 0);
  if (gesamt < 1) return 'unter 1 Mon.';
  const jahre = Math.floor(gesamt / 12);
  const monate = gesamt % 12;
  return [jahre ? `${jahre} ${jahre === 1 ? 'Jahr' : 'Jahre'}` : '', monate ? `${monate} Mon.` : ''].filter(Boolean).join(' ');
}

/** Personen im Haushalt: anzahl_bewohner, sonst Mieter + verknüpfte Mitmieter. */
export function personenImHaushalt(anzahlBewohner: unknown, mitmieter: number): number {
  const n = typeof anzahlBewohner === 'number' ? anzahlBewohner : Number(anzahlBewohner);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 1 + Math.max(0, mitmieter);
}

export function personenText(n: number): string {
  return `${n} ${n === 1 ? 'Person' : 'Personen'} im Haushalt`;
}

/** Rückstand in Monatsmieten, eine Nachkommastelle: „1 Monatsmieten“ / „1,5 Monatsmieten“ (wie im Prototyp). */
export function rueckstandMonate(saldo: number, gesamtmiete: number): string {
  if (gesamtmiete <= 0) return '';
  const m = Math.round((-saldo / gesamtmiete) * 10) / 10;
  return `${String(m).replace('.', ',')} Monatsmieten`;
}

export interface KontoAnzeige { wert: string; sub: string; rueckstand: boolean }

/** Mietkonto-Widget: Saldo oder „Ausgeglichen“, darunter Rückstand in Monatsmieten bzw. pünktliche Monate. */
export function kontoAnzeige(saldo: number, gesamtmiete: number, puenktlicheMonate: number): KontoAnzeige {
  if (saldo < -RUECKSTAND_SCHWELLE) {
    const monate = rueckstandMonate(saldo, gesamtmiete);
    return { wert: eur(saldo), sub: monate ? `Rückstand · ${monate}` : 'Rückstand', rueckstand: true };
  }
  if (saldo > RUECKSTAND_SCHWELLE) return { wert: eur(saldo, { vorzeichen: true }), sub: 'Guthaben', rueckstand: false };
  return { wert: 'Ausgeglichen', sub: `${puenktlicheMonate} ${puenktlicheMonate === 1 ? 'Monat' : 'Monate'} pünktlich`, rueckstand: false };
}

/** Monate mit Soll, die vollständig bezahlt sind (aus LedgerEntry.months). */
export function puenktlicheMonate(monate: readonly { soll: number; ist: number }[]): number {
  return monate.filter(m => m.soll > 0 && m.ist >= m.soll - 0.005).length;
}

/** Aufteilungsbalken Kalt / NK / Heizung in % (gerundet, Summe 100). */
export function mietBalken(kalt: number, nk: number, hk: number): { kalt: number; nk: number; hk: number } | null {
  const summe = kalt + nk + hk;
  if (summe <= 0) return null;
  const k = Math.round((kalt / summe) * 100);
  const n = Math.round((nk / summe) * 100);
  return { kalt: k, nk: n, hk: Math.max(0, 100 - k - n) };
}

export interface Fakten {
  vorname: string;
  name: string;
  objekt: string;
  einheit: string;
  beginn: string | null;
  zukunft: boolean;
  heute: string;
  /** null = Mietkonto nicht eingerichtet */
  saldo: number | null;
  offeneTickets: number;
  letztesTicket: string | null;
  portalAktiv: boolean;
  gekuendigtZum: string | null;
}

/** „Zusammenfassung“ ohne KI: deterministischer Text aus den Fakten (README §6, KI-Panel). */
export function zusammenfassung(f: Fakten): string {
  const ort = [f.objekt, f.einheit ? `Einheit ${f.einheit}` : ''].filter(Boolean).join(', ');
  const saetze: string[] = [];
  if (f.zukunft && f.beginn) saetze.push(`${f.name} zieht am ${deDatum(f.beginn)} ein${ort ? ` (${ort})` : ''}.`);
  else saetze.push(`${f.name} wohnt${f.beginn ? ` seit ${mietdauerKurz(f.beginn, f.heute)}` : ''}${ort ? ` in ${ort}` : ''}.`);
  if (f.gekuendigtZum) saetze.push(`Das Mietverhältnis ist zum ${deDatum(f.gekuendigtZum)} gekündigt.`);
  if (f.saldo === null) saetze.push('Zahlungsdaten liegen noch nicht vor.');
  else if (f.saldo < -RUECKSTAND_SCHWELLE) saetze.push(`Aktuell besteht ein Rückstand von ${eur(-f.saldo)}; eine Zahlungserinnerung ist sinnvoll.`);
  else saetze.push('Die Miete wird regelmäßig und pünktlich gezahlt.');
  if (f.offeneTickets > 0) saetze.push(`${f.offeneTickets} Ticket(s) sind offen${f.letztesTicket ? `, zuletzt „${f.letztesTicket}“` : ''}.`);
  else saetze.push('Es gibt keine offenen Schadensmeldungen.');
  saetze.push(f.portalAktiv ? 'Kommunikation läuft über das Mieterportal.' : 'Ein Portalzugang ist noch nicht aktiv.');
  return saetze.join(' ');
}

export interface Fakt { label: string; wert: string; ton: 'ok' | 'bad' | 'warn' | 'neutral' }

/** „Fakten“ im KI-Panel – dieselbe Grundlage wie die Zusammenfassung. */
export function kiFakten(f: Fakten & { gesamtmiete: number; kautionStatus: 'keine' | 'hinterlegt' | 'teilweise' | 'offen'; anstehendeTermine: number }): Fakt[] {
  const zahlung: Fakt = f.saldo === null
    ? { label: 'Zahlungsverhalten', wert: 'Noch keine Zahlungsdaten', ton: 'neutral' }
    : f.saldo < -RUECKSTAND_SCHWELLE
      ? { label: 'Zahlungsverhalten', wert: `Rückstand ${eur(f.saldo)}`, ton: 'bad' }
      : { label: 'Zahlungsverhalten', wert: 'Pünktlich', ton: 'ok' };
  return [
    zahlung,
    { label: 'Offene Tickets', wert: String(f.offeneTickets), ton: f.offeneTickets > 0 ? 'warn' : 'ok' },
    { label: 'Mietdauer', wert: f.zukunft ? `Einzug ${deDatum(f.beginn)}` : mietdauerKurz(f.beginn, f.heute), ton: 'neutral' },
    { label: 'Gesamtmiete', wert: eur(f.gesamtmiete), ton: 'neutral' },
    {
      label: 'Kaution',
      wert: f.kautionStatus === 'hinterlegt' ? 'vollständig' : f.kautionStatus === 'teilweise' ? 'teilweise' : f.kautionStatus === 'offen' ? 'ausstehend' : 'keine',
      ton: f.kautionStatus === 'hinterlegt' ? 'ok' : f.kautionStatus === 'keine' ? 'neutral' : 'warn',
    },
    { label: 'Mieterportal', wert: f.portalAktiv ? 'aktiv' : 'nicht aktiv', ton: f.portalAktiv ? 'ok' : 'neutral' },
    { label: 'Kündigung', wert: f.gekuendigtZum ? `zum ${deDatum(f.gekuendigtZum)}` : 'keine', ton: f.gekuendigtZum ? 'warn' : 'neutral' },
    { label: 'Anstehende Termine', wert: String(f.anstehendeTermine), ton: 'neutral' },
  ];
}
